/* Shared recovery validation. Prices are verified against Shopify, never submitted. */
(function (root) {
  'use strict';
  const DATE_KEYS = ['Fecha de entrega', 'Hora de entrega ', 'Hora de entrega', 'Dia de entrega'];
  function fail(message) { throw new Error(message); }
  function properties(value) {
    if (value === null) return {};
    if (!value || typeof value !== 'object' || Array.isArray(value)) fail('Missing properties');
    const out = Object.create(null);
    for (const [key, text] of Object.entries(value)) {
      if (!key || ['__proto__', 'constructor', 'prototype'].includes(key) || typeof text !== 'string') fail('Invalid properties');
      out[key] = text;
    }
    return out;
  }
  function lines(items) {
    if (!Array.isArray(items)) fail('Missing lines');
    return items.map(item => {
      if (item.selling_plan_allocation || item.parent_relationship || item.item_components?.length) fail('Unsupported bundle');
      const id = String(item.variant_id ?? item.id);
      if (!/^[1-9]\d{0,18}$/.test(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 100) fail('Invalid line');
      if (!Object.prototype.hasOwnProperty.call(item, 'properties')) fail('Unobserved options');
      return {id, quantity: item.quantity, properties: properties(item.properties)};
    });
  }
  function identity(item) { return JSON.stringify([item.id, Object.entries(item.properties).sort(([a], [b]) => a.localeCompare(b))]); }
  function equalLines(a, b) {
    function bag(items) {
      const result = new Map();
      for (const item of lines(items)) { const k = identity(item); result.set(k, (result.get(k) || 0) + item.quantity); }
      return result;
    }
    const x = bag(a), y = bag(b);
    return x.size === y.size && [...x].every(([k, v]) => y.get(k) === v);
  }
  function cleanAttributes(attrs) {
    return Object.fromEntries(Object.entries(properties(attrs)).filter(([key]) => !DATE_KEYS.includes(key) && !key.startsWith('_pf_recovery')));
  }
  function equalObject(a, b) {
    return JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort());
  }
  function sameGift(snapshot, cart) {
    return equalLines(snapshot.items, cart.items) && snapshot.note === (cart.note ?? '') && equalObject(cleanAttributes(snapshot.attributes), cleanAttributes(cart.attributes));
  }
  function capture(cart, language, allowEmpty = false) {
    if (!cart || !Object.hasOwn(cart, 'note') || !Object.hasOwn(cart, 'attributes')) fail('Incomplete basket');
    if (cart.note !== null && typeof cart.note !== 'string') fail('Unobserved note');
    if (cart.currency !== 'EUR') fail('Unsupported currency');
    const mapped = lines(cart.items);
    if ((!mapped.length && !allowEmpty) || mapped.length > 40) fail('Empty or oversized basket');
    return {
      version: 1, currency: 'EUR', language,
      note: cart.note ?? '', attributes: properties(cart.attributes),
      discounts: [...new Set((cart.cart_level_discount_applications || []).filter(d => d.type === 'discount_code').map(d => d.title))],
      items: mapped.map((line, n) => {
        const source = cart.items[n];
        if (!Number.isSafeInteger(source.original_price)) fail('Missing option price');
        return {...line, ...(source.handle ? {handle:source.handle} : {}), title: source.product_title || source.title, variant_title: source.variant_title || '', original_price: source.original_price};
      })
    };
  }
  function verifyPrices(snapshot, cart) {
    const prices = new Map(snapshot.items.map(item => [identity(item), item.original_price]));
    for (const [n, item] of lines(cart.items).entries()) {
      if (prices.get(identity(item)) !== cart.items[n].original_price) fail('Option price changed; select the item again');
    }
  }
  function verify(snapshot, cart) {
    if (!sameGift(snapshot, cart)) fail('Gift options, quantities or note changed');
    verifyPrices(snapshot, cart);
    if (DATE_KEYS.some(key => cart.attributes[key])) fail('Old delivery selection was retained');
  }
  function updateDetails(snapshot, currentAttributes) {
    const attributes = Object.fromEntries(Object.keys(currentAttributes || {}).map(k => [k, '']));
    Object.assign(attributes, cleanAttributes(snapshot.attributes));
    DATE_KEYS.forEach(k => { attributes[k] = ''; });
    return {attributes, note: snapshot.note, discount: snapshot.discounts.join(',')};
  }
  async function replace(snapshot, current, api, saveBackup) {
    // Customer's choice must happen before calling this function. GET alone never mutates.
    const backup = capture(current, snapshot.language, true);
    await saveBackup(backup);
    let changed = false;
    try {
      if (!equalLines(snapshot.items, current.items)) {
        changed = true;
        await api('clear', {});
        await api('add', {items: lines(snapshot.items)});
      }
      changed = true;
      await api('update', updateDetails(snapshot, current.attributes));
      const readback = await api('read');
      verify(snapshot, readback);
      return readback;
    } catch (error) {
      if (changed) {
        try {
          await api('clear', {});
          if (backup.items.length) await api('add', {items: lines(backup.items)});
          const partial = await api('read');
          const attributes = Object.fromEntries(Object.keys(partial.attributes || {}).map(k => [k, '']));
          Object.assign(attributes, backup.attributes);
          await api('update', {attributes, note: backup.note, discount: backup.discounts.join(',')});
          const restored = await api('read');
          if (!equalLines(backup.items, restored.items) || backup.note !== (restored.note ?? '')) throw new Error('Rollback differs');
          const nonempty = a => Object.fromEntries(Object.entries(a).filter(([,v]) => v !== ''));
          if (!equalObject(nonempty(backup.attributes), nonempty(restored.attributes))) throw new Error('Rollback attributes differ');
          verifyPrices(backup, restored);
          error.rollback = 'restored';
        } catch (_) { error.rollback = 'needs_customer_help'; }
      }
      throw error;
    }
  }
  function madridDay(now = new Date()) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en', {timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(now).map(p => [p.type, p.value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  }
  function validDate(value, now = new Date()) {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || '');
    if (!m) return false;
    const iso = `${m[3]}-${m[2]}-${m[1]}`, date = new Date(iso+'T12:00:00Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0,10) === iso && iso >= madridDay(now);
  }
  const api = {DATE_KEYS, properties, lines, equalLines, cleanAttributes, sameGift, capture, verify, replace, validDate, madridDay};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.PersaGiftCore = api;
})(typeof window === 'undefined' ? {} : window);
